import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Check, Shield, ArrowRight, ArrowLeft, Download, Info, AlertCircle, XCircle, Loader2, CalendarPlus } from 'lucide-react';

import { BACKEND_URL } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { useAuth } from '../contexts/AuthContext';
import { CheckoutAuthGate } from '../components/CheckoutAuthGate';

/* ===== Booking Fee Tiers (mirrors backend) =====
   Free tickets (R0) NEVER incur a curation fee — must match backend logic
   in /app/backend/server.py :: calculate_booking_fee. */
const calculateBookingFeeBase = (price) => {
  if (price <= 0) return 0;
  if (price < 150) return 10;
  if (price < 300) return 12;
  if (price < 600) return 15;
  return 20;
};

/* Apply the event's strategy to the full booking fee.
   Returns the portion the CUSTOMER pays on top of the ticket price.
   VinePass always receives the full fee — any portion not paid by the
   customer is absorbed out of the organiser's payout. */
const customerPaidFeeForStrategy = (fullFee, strategy) => {
  if (fullFee <= 0) return 0;
  if (strategy === 'organiser_absorbs') return 0;
  if (strategy === 'split_50_50') return Math.round((fullFee / 2) * 100) / 100;
  return fullFee; // customer_pays / default
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, clearCart } = useCart();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 0=Auth, 1=Details, 2=Payment, 3=Result
  const [feesEnabled, setFeesEnabled] = useState(true);

  // Every booking requires a signed-in VinePass account. Once we know
  // whether the guest is authenticated, decide ONCE whether to show the
  // auth gate (step 0) or skip straight to Details — but never re-decide
  // after that (a returning-guest login inside the gate advances the step
  // explicitly via handleAuthenticated, not this effect).
  const initialStepDecided = useRef(false);
  useEffect(() => {
    if (searchParams.get('order_id')) return; // handled by the payment-return effect below
    if (authLoading) return;
    if (initialStepDecided.current) return;
    initialStepDecided.current = true;
    setStep(user ? 1 : 0);
  }, [authLoading, user, searchParams]);

  const handleAuthenticated = () => setStep(1);

  // Fetch the public runtime config (booking-fee toggle) on mount
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/config`)
      .then((r) => setFeesEnabled(r.data?.booking_fees_enabled !== false))
      .catch(() => setFeesEnabled(true));
  }, []);

  const calculateBookingFee = (price) => (feesEnabled ? calculateBookingFeeBase(price) : 0);
  // Customer-facing fee considers the cart item's event strategy. We default
  // to customer_pays whenever the field isn't snapshotted on the event yet.
  const customerPaidFee = (price, event) => {
    const full = calculateBookingFee(price);
    const strategy = (event && event.booking_fee_strategy) || 'customer_pays';
    return customerPaidFeeForStrategy(full, strategy);
  };
  const [formData, setFormData] = useState({ name: '', email: '', reminder_opt_in: true });

  // Prefill the buyer's details from their account once authenticated —
  // "do not require tickets to be selected again" extends to not re-asking
  // for a name/email we already have.
  useEffect(() => {
    if (user && !formData.name && !formData.email) {
      setFormData((f) => ({ ...f, name: user.name || f.name, email: user.email || f.email }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Multi-ticket guest details. `useSameForAll` defaults ON so single-ticket
  // checkouts and buyers who don't care still just see the existing flow.
  // `guestDetails` is keyed by cart index → array(quantity) of {name,email}.
  const totalSeats = cart.reduce((s, it) => s + (it.quantity || 0), 0);
  const hasMultipleSeats = totalSeats > 1;
  const [useSameForAll, setUseSameForAll] = useState(true);
  const [guestDetails, setGuestDetails] = useState({});

  // Initialise guestDetails when cart changes (keep existing values where shapes match)
  useEffect(() => {
    setGuestDetails((prev) => {
      const next = {};
      cart.forEach((item, idx) => {
        const existing = prev[idx] || [];
        next[idx] = Array.from({ length: item.quantity }, (_, i) => ({
          name: existing[i]?.name || '',
          email: existing[i]?.email || '',
        }));
      });
      return next;
    });
  }, [cart]);

  const updateGuestField = (cartIdx, seatIdx, field, value) => {
    setGuestDetails((prev) => {
      const itemSeats = [...(prev[cartIdx] || [])];
      itemSeats[seatIdx] = { ...itemSeats[seatIdx], [field]: value };
      return { ...prev, [cartIdx]: itemSeats };
    });
  };

  // Result-screen state
  // paymentState: 'verifying' | 'success' | 'pending' | 'cancelled' | 'failed' | 'timeout'
  const [paymentState, setPaymentState] = useState('verifying');
  const [confirmedOrderId, setConfirmedOrderId] = useState(null);
  const [confirmedEventId, setConfirmedEventId] = useState(null);
  const pollRef = useRef(null);

  const paymentMethodParam = searchParams.get('payment_method');
  const orderIdParam = searchParams.get('order_id');
  const cancelledParam = searchParams.get('cancelled');

  // Fire ticket_purchased ONCE when payment is confirmed. Triggered by the
  // success-state transition so it covers both PayFast (ITN-confirmed via
  // polling) and free-RSVP flows.
  const purchaseTrackedRef = useRef(false);
  useEffect(() => {
    if (paymentState !== 'success') return;
    if (purchaseTrackedRef.current) return;
    if (!confirmedOrderId) return;
    purchaseTrackedRef.current = true;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/orders/${confirmedOrderId}/status`);
        trackEvent('ticket_purchased', {
          order_id: confirmedOrderId,
          event_id: res.data?.event_id,
          amount: Number(res.data?.total_amount ?? 0),
          payment_method: paymentMethodParam || 'unknown',
        });
      } catch {
        // Still emit the conversion — without amount details if status fetch fails
        trackEvent('ticket_purchased', {
          order_id: confirmedOrderId,
          payment_method: paymentMethodParam || 'unknown',
        });
      }
    })();
  }, [paymentState, confirmedOrderId, paymentMethodParam]);

  // Fire checkout_start ONCE when the user lands on checkout with items.
  // Guarded by a ref so React 18 dev-mode double-mount doesn't double-count,
  // and only if cart has items (avoids firing on the empty-cart screen or
  // when the user lands on /checkout via a return-from-PayFast URL).
  const checkoutStartTrackedRef = useRef(false);
  useEffect(() => {
    if (checkoutStartTrackedRef.current) return;
    if (paymentMethodParam) return; // returning from payment, not starting
    if (!cart || cart.length === 0) return;
    checkoutStartTrackedRef.current = true;
    const totalItems = cart.reduce((sum, it) => sum + (it.quantity || 0), 0);
    const totalValue = cart.reduce(
      (sum, it) => sum + (it.ticketType?.price || 0) * (it.quantity || 0), 0,
    );
    trackEvent('checkout_start', {
      ticket_types: cart.length,
      quantity: totalItems,
      cart_value: Number(totalValue.toFixed(2)),
    });
  }, [cart, paymentMethodParam]);

  /* ----- Handle return from PayFast (paid) OR direct free-order success ----- */
  useEffect(() => {
    if (!orderIdParam) return;
    if (paymentMethodParam !== 'payfast' && paymentMethodParam !== 'free') return;
    setStep(3);
    setConfirmedOrderId(orderIdParam);

    if (cancelledParam === '1') {
      setPaymentState('cancelled');
      // PayFast's cancel_url is a plain redirect — it does NOT send an ITN,
      // so the backend never hears about this cancellation on its own. Tell
      // it explicitly so the reserved seats are released back to inventory
      // (otherwise this ticket type stays falsely "sold out" for everyone).
      axios.post(`${BACKEND_URL}/api/orders/${orderIdParam}/cancel`).catch(() => {
        // Non-fatal — the stale-pending sweep will release it later regardless.
      });
      return;
    }

    // Free-ticket fast path: order was already marked completed at /api/orders.
    // Skip the PayFast verification polling entirely.
    if (paymentMethodParam === 'free') {
      (async () => {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/orders/${orderIdParam}/status`);
          if (res.data?.event_id) setConfirmedEventId(res.data.event_id);
        } catch {
          // event_id is only used for the add-to-calendar button; non-fatal
        }
        setPaymentState('success');
        clearCart();
      })();
      return;
    }

    // Verify order status via polling — PayFast ITN updates happen server-side
    let attempts = 0;
    const maxAttempts = 40; // ~2 min at 3s interval (handles slower ITN arrival)
    setPaymentState('verifying');

    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/orders/${orderIdParam}/status`);
        const status = res.data?.status;
        if (res.data?.event_id) setConfirmedEventId(res.data.event_id);
        if (status === 'completed') {
          setPaymentState('success');
          clearCart();
          return;
        }
        if (status === 'failed') {
          setPaymentState('failed');
          return;
        }
        if (status === 'cancelled') {
          setPaymentState('cancelled');
          return;
        }
      } catch {
        // keep polling — transient network errors are OK
      }
      if (attempts >= maxAttempts) {
        setPaymentState('pending');
        return;
      }
      pollRef.current = setTimeout(poll, 3000);
    };
    poll();

    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodParam, orderIdParam, cancelledParam]);

  /* ----- Totals ----- */
  const subtotal = cart.reduce((sum, item) => sum + item.ticketType.price * item.quantity, 0);
  // Total fees VinePass receives (always the full fee per ticket — for display only)
  const totalFees = cart.reduce(
    (sum, item) => sum + calculateBookingFee(item.ticketType.price) * item.quantity,
    0
  );
  // What the CUSTOMER actually pays in fees, depending on each event's strategy
  const totalCustomerPaidFees = cart.reduce(
    (sum, item) => sum + customerPaidFee(item.ticketType.price, item.event) * item.quantity,
    0
  );
  const grandTotal = subtotal + totalCustomerPaidFees;

  const handleProceedToPayment = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    // When the buyer chose "Different guest per ticket", every name field must be filled.
    if (hasMultipleSeats && !useSameForAll) {
      for (let ci = 0; ci < cart.length; ci += 1) {
        const seats = guestDetails[ci] || [];
        for (let si = 0; si < seats.length; si += 1) {
          if (!seats[si]?.name?.trim()) {
            toast.error('Please enter a name for every ticket');
            return;
          }
        }
      }
    }
    setStep(2);
  };

  const handleSubmitPayment = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    setLoading(true);
    try {
      const orderItems = cart.map((item, idx) => {
        const seats = guestDetails[idx] || [];
        // When useSameForAll is ON (or only 1 seat in the whole order), omit
        // the guests array entirely so the backend falls back to the buyer
        // name for every seat — preserves the legacy single-ticket flow.
        const sendGuests = hasMultipleSeats && !useSameForAll;
        const guests = sendGuests
          ? seats.slice(0, item.quantity).map((s) => ({
              name: (s?.name || '').trim() || formData.name,
              ...(s?.email?.trim() ? { email: s.email.trim() } : {}),
            }))
          : undefined;
        return {
          event_id: item.event.event_id,
          ticket_type_id: item.ticketType.ticket_id,
          quantity: item.quantity,
          ...(guests ? { guests } : {}),
        };
      });

      // 1. Create the pending order on our backend
      const orderResponse = await axios.post(
        `${BACKEND_URL}/api/orders`,
        {
          items: orderItems,
          customer_name: formData.name,
          customer_email: formData.email,
          reminder_opt_in: formData.reminder_opt_in,
        },
        { withCredentials: true }
      );
      const orderId = orderResponse.data.order_id;

      // 1b. Free-ticket fast path — backend already marked the order
      // completed and triggered ticket generation + confirmation email.
      // Skip PayFast entirely and route straight to the success screen.
      if (
        orderResponse.data.status === 'completed' ||
        Number(orderResponse.data.total_amount) === 0
      ) {
        navigate(`/checkout?payment_method=free&order_id=${orderId}`, { replace: true });
        return;
      }

      // 2. Ask backend for PayFast form data + signature
      const paymentResponse = await axios.post(`${BACKEND_URL}/api/payments/payfast/checkout`, {
        order_id: orderId,
        origin_url: window.location.origin,
      });

      // 3. Auto-submit a hidden form to PayFast (secure redirect pattern)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = paymentResponse.data.payment_url;
      Object.entries(paymentResponse.data.form_data).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      // Build a safe string message from any shape of backend response —
      // Pydantic 422 responses come back as an array which would crash the toast.
      let msg;
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map((e) => e.msg || JSON.stringify(e)).join(', ');
      } else if (error.response?.status) {
        msg = `Checkout failed (HTTP ${error.response.status})`;
      } else if (error.message) {
        msg = `Checkout failed: ${error.message}`;
      } else {
        msg = 'Checkout failed. Please try again.';
      }
      console.error('[Checkout] error', error);
      toast.error(msg);
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!confirmedOrderId) return;
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/orders/${confirmedOrderId}/tickets/pdf`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `VinePass_Tickets_${confirmedOrderId.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download PDF. Please try again.');
    }
  };

  const handleAddToCalendar = () => {
    if (!confirmedEventId) return;
    window.location.href = `${BACKEND_URL}/api/events/${confirmedEventId}/ics`;
  };

  const retryCheckout = () => {
    // Reset result screen and send user back to the payment step (fresh cart entry)
    navigate('/cart');
  };

  /* ======================================================================
     STEP 3 — Result screen (verifying / success / pending / cancelled / failed)
     ====================================================================== */
  if (step === 3) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center bg-white rounded-2xl p-10 shadow-lg border border-[#E5E9E6]" data-testid="checkout-result">

          {paymentState === 'verifying' && (
            <>
              <Loader2 className="w-14 h-14 text-[#1F3D2B] mx-auto mb-6 animate-spin" />
              <h2 className="text-2xl font-bold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="verifying-title">
                Confirming Your Payment
              </h2>
              <p className="text-[#4A5D50] text-sm">
                Waiting for PayFast to confirm your booking… this usually takes a few seconds.
              </p>
            </>
          )}

          {paymentState === 'success' && (
            <>
              <div className="w-16 h-16 bg-[#1F3D2B] rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="confirmation-title">
                Booking Confirmed
              </h2>
              <p className="text-[#4A5D50] mb-2">
                {paymentMethodParam === 'free'
                  ? 'Your RSVP is confirmed — your tickets are on the way to your inbox.'
                  : 'Your payment was received and your tickets are on the way to your inbox.'}
              </p>
              <p className="text-sm text-[#4A5D50] mb-8">
                Present the QR code at the event entrance for entry.
              </p>
              <Button
                onClick={handleDownloadPDF}
                className="w-full bg-[#C9A27A] text-white hover:bg-[#B8926B] rounded-full px-10 py-3 mb-3"
                data-testid="download-pdf-ticket-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF Tickets
              </Button>
              {confirmedEventId && (
                <Button
                  onClick={handleAddToCalendar}
                  variant="outline"
                  className="w-full rounded-full px-10 py-3 mb-3 border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#F4F6F3]"
                  data-testid="add-to-calendar-button"
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Add to Calendar
                </Button>
              )}
              <Button
                onClick={() => navigate('/my-tickets')}
                variant="outline"
                className="w-full rounded-full px-10 py-3 mb-3 border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#F4F6F3]"
                data-testid="view-my-tickets-button"
              >
                View My Tickets
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="w-full text-[#4A5D50] hover:text-[#1F3D2B] rounded-full"
                data-testid="back-to-home-button"
              >
                Back to Home
              </Button>
            </>
          )}

          {paymentState === 'pending' && (
            <>
              <div className="w-16 h-16 bg-[#C9A27A] rounded-full flex items-center justify-center mx-auto mb-6">
                <Info className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="pending-title">
                Payment Pending
              </h2>
              <p className="text-[#4A5D50] text-sm mb-6">
                Your payment is still being processed by PayFast. We'll email you as soon as it's confirmed — no need to try again.
              </p>
              <Button onClick={() => navigate('/my-tickets')} className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full" data-testid="check-tickets-later-button">
                Check My Tickets Later
              </Button>
            </>
          )}

          {paymentState === 'cancelled' && (
            <>
              <div className="w-16 h-16 bg-[#C9A27A] rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="cancelled-title">
                Payment Cancelled
              </h2>
              <p className="text-[#4A5D50] text-sm mb-6">
                You cancelled the PayFast checkout. Your tickets are still available — you can try again whenever you're ready.
              </p>
              <Button onClick={retryCheckout} className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full mb-3" data-testid="retry-checkout-button">
                Try Again
              </Button>
              <Button onClick={() => navigate('/events')} variant="ghost" className="w-full text-[#4A5D50] hover:text-[#1F3D2B] rounded-full">
                Browse Events
              </Button>
            </>
          )}

          {paymentState === 'failed' && (
            <>
              <div className="w-16 h-16 bg-[#B04D4D] rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#112318] mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="failed-title">
                Payment Declined
              </h2>
              <p className="text-[#4A5D50] text-sm mb-6">
                Your bank declined this payment and you have not been charged. Please try again with a different card or method.
              </p>
              <Button onClick={retryCheckout} className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full mb-3" data-testid="retry-failed-button">
                Try Again
              </Button>
              <Button onClick={() => navigate('/')} variant="ghost" className="w-full text-[#4A5D50] hover:text-[#1F3D2B] rounded-full">
                Back to Home
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  /* ======================================================================
     Empty-cart guard (but not while we're on the result screen)
     ====================================================================== */
  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#4A5D50] mb-4">Your cart is empty</p>
          <Button onClick={() => navigate('/events')} className="bg-[#1F3D2B] text-white rounded-full">
            Browse Events
          </Button>
        </div>
      </div>
    );
  }

  /* ======================================================================
     STEP 0 — Smart single-screen auth gate. Every booking requires a
     VinePass account; this is skipped entirely (see the effect above) for
     guests who already have an active session.
     ====================================================================== */
  if (step === 0) {
    return <CheckoutAuthGate onAuthenticated={handleAuthenticated} />;
  }

  /* ======================================================================
     STEPS 1 & 2 — Details + Payment
     ====================================================================== */
  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12" data-testid="checkout-steps">
          {[
            { num: 1, label: 'Details' },
            { num: 2, label: 'Payment' },
            { num: 3, label: 'Confirmation' },
          ].map((s, i) => (
            <React.Fragment key={s.num}>
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                    step >= s.num ? 'bg-[#1F3D2B] text-white' : 'bg-[#E5E9E6] text-[#4A5D50]'
                  }`}
                >
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className={`ml-2 text-sm font-medium hidden sm:block ${step >= s.num ? 'text-[#1F3D2B]' : 'text-[#4A5D50]'}`}>
                  {s.label}
                </span>
              </div>
              {i < 2 && (
                <div className={`w-12 sm:w-24 h-0.5 mx-2 sm:mx-4 transition-colors ${step > s.num ? 'bg-[#1F3D2B]' : 'bg-[#E5E9E6]'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <p className="text-center text-[#4A5D50] text-sm mb-8">
          You're one step away from a great experience
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* Main Panel */}
          <div className="lg:col-span-3">

            {step === 1 && (
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E9E6]">
                <h2 className="text-2xl font-semibold text-[#112318] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Your Details
                </h2>
                <form onSubmit={handleProceedToPayment} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-[#4A5D50] text-sm">Full Name</Label>
                    <Input id="name" type="text" placeholder="Jane Doe" autoComplete="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required className="mt-1" data-testid="checkout-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[#4A5D50] text-sm">Email Address</Label>
                    <Input id="email" type="email" placeholder="jane@example.com" autoComplete="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required className="mt-1" data-testid="checkout-email-input" />
                    <p className="text-xs text-[#4A5D50] mt-1">Tickets & QR codes will be sent here</p>
                  </div>

                  {/* Multi-ticket guest details — only when there's more than one seat */}
                  {hasMultipleSeats && (
                    <div className="bg-[#F4F6F3] border border-[#E5E9E6] rounded-xl p-5" data-testid="guest-details-section">
                      <div className="flex items-center justify-between mb-3 gap-3">
                        <div>
                          <p className="text-sm font-medium text-[#112318]">Guest names</p>
                          <p className="text-xs text-[#4A5D50] mt-0.5">
                            Print each guest&apos;s name on their ticket — useful for guest lists and door checks.
                          </p>
                        </div>
                      </div>
                      <label className="flex items-start gap-3 cursor-pointer select-none mb-4" data-testid="use-same-details-toggle">
                        <input
                          type="checkbox"
                          checked={useSameForAll}
                          onChange={(e) => setUseSameForAll(e.target.checked)}
                          className="mt-1 w-4 h-4 accent-[#1F3D2B] cursor-pointer"
                          data-testid="use-same-details-checkbox"
                        />
                        <span className="text-sm text-[#112318]">Use my details for all tickets</span>
                      </label>

                      {!useSameForAll && (
                        <div className="space-y-5" data-testid="per-ticket-guest-fields">
                          {cart.map((item, ci) => (
                            <div key={ci} className="space-y-3">
                              <p className="text-[10px] uppercase tracking-[0.15em] text-[#C9A27A]">
                                {item.event.title} · {item.ticketType.type_name}
                              </p>
                              {(guestDetails[ci] || []).map((seat, si) => (
                                <div key={si} className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-white border border-[#E5E9E6] rounded-lg p-3">
                                  <div>
                                    <Label className="text-[#4A5D50] text-xs">Guest {si + 1} name</Label>
                                    <Input
                                      type="text"
                                      placeholder="Full name"
                                      value={seat?.name || ''}
                                      onChange={(e) => updateGuestField(ci, si, 'name', e.target.value)}
                                      required
                                      className="mt-1"
                                      data-testid={`guest-name-input-${ci}-${si}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-[#4A5D50] text-xs">Email <span className="text-[#9AA89F]">(optional)</span></Label>
                                    <Input
                                      type="email"
                                      placeholder="guest@example.com"
                                      value={seat?.email || ''}
                                      onChange={(e) => updateGuestField(ci, si, 'email', e.target.value)}
                                      className="mt-1"
                                      data-testid={`guest-email-input-${ci}-${si}`}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                          <p className="text-[11px] text-[#4A5D50]">
                            All tickets will be delivered to <strong>{formData.email || 'your email'}</strong> in a single confirmation.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 24h reminder opt-in */}
                  <label
                    className="flex items-start gap-3 cursor-pointer select-none bg-[#F4F6F3] border border-[#E5E9E6] rounded-xl p-4 hover:border-[#1F3D2B] transition-colors"
                    data-testid="reminder-opt-in-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={formData.reminder_opt_in}
                      onChange={(e) => setFormData({ ...formData, reminder_opt_in: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-[#1F3D2B] cursor-pointer"
                      data-testid="reminder-opt-in-checkbox"
                    />
                    <span>
                      <span className="block text-sm font-medium text-[#112318]">Remind me 24 hours before the event</span>
                      <span className="block text-xs text-[#4A5D50] mt-0.5">
                        We'll send you a gentle reminder the day before, with a calendar invite attached.
                      </span>
                    </span>
                  </label>

                  <Button type="submit"
                    className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-6 text-lg font-medium transition-all shadow-md mt-4"
                    data-testid="continue-to-payment-button">
                    Continue to Payment
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              </div>
            )}

            {step === 2 && (
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E5E9E6]">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-[#112318]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {grandTotal > 0 ? 'Payment Method' : 'Confirm Your RSVP'}
                  </h2>
                  <button
                    onClick={() => setStep(1)}
                    className="text-sm text-[#4A5D50] hover:text-[#1F3D2B] flex items-center"
                    data-testid="back-to-details-button"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" /> Edit Details
                  </button>
                </div>

                {grandTotal > 0 ? (
                  <>
                    <div className="space-y-3 mb-6">
                      <div className="border-2 rounded-xl p-5 border-[#1F3D2B] bg-[#F4F6F3]" data-testid="payfast-payment-option">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center border-[#1F3D2B] bg-[#1F3D2B]">
                              <div className="w-2 h-2 bg-white rounded-full" />
                            </div>
                            <div>
                              <span className="font-medium text-[#112318]">PayFast</span>
                              <p className="text-xs text-[#4A5D50]">Card, Instant EFT, Capitec Pay, SnapScan &amp; more</p>
                            </div>
                          </div>
                          <span className="text-xs text-[#4A5D50] bg-white border border-[#E5E9E6] px-2 py-1 rounded">South Africa</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-[#4A5D50] text-xs mb-6 justify-center">
                      <Shield className="w-4 h-4" />
                      <span>Secure payment powered by PayFast · 256-bit SSL</span>
                    </div>
                  </>
                ) : (
                  <div
                    className="border-2 rounded-xl p-5 border-emerald-200 bg-emerald-50/40 mb-6"
                    data-testid="free-rsvp-notice"
                  >
                    <p className="text-sm font-medium text-emerald-800 mb-1">
                      This is a complimentary booking — no payment required.
                    </p>
                    <p className="text-xs text-[#4A5D50]">
                      Confirm below to reserve your spot. We&apos;ll send your QR ticket straight to your inbox.
                    </p>
                  </div>
                )}

                <Button onClick={handleSubmitPayment} disabled={loading}
                  className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-6 text-lg font-medium transition-all shadow-md"
                  data-testid="secure-ticket-button">
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      {grandTotal > 0 ? 'Redirecting to PayFast…' : 'Confirming RSVP…'}
                    </>
                  ) : (
                    <>
                      <Lock className="mr-2 w-5 h-5" />
                      {grandTotal > 0 ? `Secure Your Spot · R${grandTotal.toFixed(2)}` : 'Confirm Free RSVP'}
                    </>
                  )}
                </Button>

                <p className="text-center text-xs text-[#4A5D50] mt-4">
                  {grandTotal > 0
                    ? "You'll be redirected to PayFast to complete your payment."
                    : "You'll receive your QR ticket by email moments after confirming."}
                </p>
              </div>
            )}
          </div>

          {/* Side Panel: Order Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-7 shadow-sm border border-[#E5E9E6] sticky top-24">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A27A] mb-2">Your Reservation</p>
              <h3 className="text-2xl font-semibold text-[#112318] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Order Summary
              </h3>

              <div className="space-y-5 mb-6" data-testid="checkout-order-items">
                {cart.map((item, index) => {
                  const fee = calculateBookingFee(item.ticketType.price);
                  const customerFee = customerPaidFee(item.ticketType.price, item.event);
                  const isFree = item.ticketType.price <= 0;
                  const strategy = (item.event && item.event.booking_fee_strategy) || 'customer_pays';
                  return (
                    <div key={index} className="last:pb-0">
                      <p className="font-medium text-[#112318] text-sm mb-1">{item.event.title}</p>
                      <p className="text-xs text-[#4A5D50] mb-3">
                        {item.ticketType.type_name} · ×{item.quantity}
                        {isFree && <span className="ml-2 inline-block bg-emerald-50 text-emerald-700 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full">Free</span>}
                      </p>
                      <div className="flex justify-between text-xs text-[#4A5D50] mb-1">
                        <span>Experience</span>
                        <span>{isFree ? 'Free' : `R${(item.ticketType.price * item.quantity).toFixed(2)}`}</span>
                      </div>
                      {!isFree && customerFee > 0 && (
                        <div className="flex justify-between text-xs text-[#4A5D50]">
                          <span>VinePass Curation Fee</span>
                          <span>R{(customerFee * item.quantity).toFixed(2)}</span>
                        </div>
                      )}
                      {!isFree && fee > 0 && customerFee < fee && (
                        <div className="flex justify-between text-[10px] italic text-[#4A5D50]/80 mt-0.5">
                          <span>{strategy === 'organiser_absorbs' ? 'Curation fee absorbed by organiser' : 'Curation fee split with organiser'}</span>
                          <span>Included</span>
                        </div>
                      )}
                      {/* gold separator between items */}
                      {index < cart.length - 1 && (
                        <div className="mt-4 h-px bg-gradient-to-r from-transparent via-[#C9A27A]/40 to-transparent" />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Subtotals — only when paid items exist */}
              <div className="h-px bg-gradient-to-r from-transparent via-[#C9A27A]/50 to-transparent mb-4" />
              <div className="space-y-2.5 mb-4">
                <div className="flex justify-between text-sm text-[#4A5D50]">
                  <span>Subtotal</span>
                  <span>R{subtotal.toFixed(2)}</span>
                </div>
                {totalCustomerPaidFees > 0 && (
                  <div className="flex justify-between text-sm text-[#4A5D50]">
                    <span>VinePass Curation Fee</span>
                    <span data-testid="checkout-curation-fee">R{totalCustomerPaidFees.toFixed(2)}</span>
                  </div>
                )}
                {totalFees > totalCustomerPaidFees && (
                  <div className="flex justify-between items-baseline text-[11px] text-[#4A5D50]/80">
                    <span className="italic">
                      {totalCustomerPaidFees === 0 ? 'Booking fee absorbed by organiser' : 'Booking fee split with organiser'}
                    </span>
                    <span className="italic">No extra charge to you</span>
                  </div>
                )}
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-[#C9A27A]/70 to-transparent mb-4" />
              <div className="flex justify-between items-baseline">
                <span className="text-sm uppercase tracking-[0.15em] text-[#4A5D50]">Total</span>
                <span data-testid="checkout-total" className="text-2xl font-semibold text-[#1F3D2B]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  R{grandTotal.toFixed(2)}
                </span>
              </div>

              <div className="mt-7 space-y-2 text-xs text-[#4A5D50]">
                <div className="flex items-center gap-2"><Lock className="w-3.5 h-3.5" /><span>Secure payment · PayFast</span></div>
                <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5" /><span>Instant email confirmation</span></div>
                <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5" /><span>Digital tickets with QR code</span></div>
              </div>
              <p className="mt-4 pt-3 border-t border-[#E5E9E6] text-[10px] text-[#9AA89F] leading-relaxed">
                Refunds (when approved) are processed within <strong className="text-[#4A5D50]">7–10 business days</strong>. Booking fees are non-refundable.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
